import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import { motion, AnimatePresence } from "framer-motion";
import { Role } from "./types/permissions";
import AddRoleModal from "./AddRole";
import PermissionsManagement from "./PermissionsManagement";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";
import roleService from "../../../service/roleService";
import { toast } from "react-toastify";

const RoleCard: React.FC<{
  role: Role;
  onManagePermissions: (role: Role) => void;
  onDelete: (role: Role) => void;
  index: number;
}> = ({ role, onManagePermissions, onDelete, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, delay: index * 0.1 }}
    whileHover={{ y: -4 }}
  >
    <Card sx={{ position: "relative", boxShadow: 1, height: "100%" }}>
      <CardContent>
        {role.name !== "Admin" && (
          <IconButton
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(role);
            }}
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              backgroundColor: "background.paper",
              borderRadius: "50%",
              "&:hover": {
                backgroundColor: "error.10",
              },
            }}
          >
            <DeleteIcon fontSize="small" sx={{ color: "#D1A077" }} />
          </IconButton>
        )}
        <Typography variant="h6" fontWeight="bold" mb={1}>
          {role.name}
        </Typography>
        <Box display="flex" alignItems="center" mt={2}>
          <LockOutlinedIcon
            fontSize="small"
            sx={{ mr: 1, color: "primary.main" }}
          />
          <Typography variant="body2">
            {role.permissions.length} permission
            {role.permissions.length !== 1 ? "s" : ""}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={() => onManagePermissions(role)}
          sx={{ mt: 2, width: "100%" }}
        >
          Manage Permissions
        </Button>
      </CardContent>
    </Card>
  </motion.div>
);

const RolesPermissions: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [addRoleModalOpen, setAddRoleModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [showPermissionsView, setShowPermissionsView] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);

  const reduxTheme = useSelector((state: RootState) => state.theme);

  const loadRoles = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await roleService.getAllRoles();
      setRoles(data);
    } catch (err) {
      console.error("Failed to load roles", err);
      setError("Failed to load roles. Please try again.");
      toast.error("Failed to load roles", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
  }, []);

  const handleAddRole = async (roleData: { name: string }) => {
    try {
      const newRole = await roleService.createRole({ name: roleData.name });
      setRoles((prev) => [...prev, newRole]);
      setAddRoleModalOpen(false);
      toast.success(`Role "${newRole.name}" created successfully`, {
        position: "top-right",
      });
    } catch (err) {
      console.error("Failed to create role", err);
      toast.error("Failed to create role", { position: "top-right" });
    }
  };

  const handleManagePermissions = (role: Role) => {
    setSelectedRole(role);
    setShowPermissionsView(true);
  };

  const handleBackToRoles = () => {
    setShowPermissionsView(false);
    setSelectedRole(null);
  };

  const handleUpdatePermissions = async (
    roleId: number,
    permissionIds: number[]
  ) => {
    try {
      const updatedRole = await roleService.updateRole(roleId, {
        permissions: permissionIds,
      });
      setRoles((prev) =>
        prev.map((role) => (role.id === roleId ? updatedRole : role))
      );
      setShowPermissionsView(false);
      setSelectedRole(null);
      toast.success("Permissions saved successfully", {
        position: "top-right",
      });
    } catch (err) {
      console.error("Failed to save permissions:", err);
      toast.error("Failed to save permissions", { position: "top-right" });
    }
  };

  const handleDeleteRoleClick = (role: Role) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!roleToDelete) return;

    try {
      await roleService.deleteRole(roleToDelete.id);
      setRoles((prev) => prev.filter((r) => r.id !== roleToDelete.id));
      toast.success(`Role "${roleToDelete.name}" deleted successfully`, {
        position: "top-right",
      });
    } catch (err) {
      console.error("Failed to delete role:", err);
      toast.error("Failed to delete role", { position: "top-right" });
    } finally {
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
    }
  };

  if (loading && !error) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <AnimatePresence mode="wait">
        {!showPermissionsView ? (
          <motion.div
            key="roles-list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={loadRoles}
                    sx={{ textTransform: "none", fontWeight: "bold" }}
                  >
                    Retry
                  </Button>
                }
              >
                {error}
              </Alert>
            )}

            <Paper
              sx={{
                boxShadow: 0,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
                p: 3,
                color: reduxTheme.primary_colors[100],
              }}
            >
              <Box>
                <Typography variant="h4" fontWeight="bold" mb={1}>
                  Roles & Permissions
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Review your members roles and allocate permissions
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="large"
                onClick={() => setAddRoleModalOpen(true)}
                sx={{ backgroundColor: reduxTheme.primary_colors[400] }}
              >
                Add New Role
              </Button>
            </Paper>

            {roles.length === 0 && !loading ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Typography variant="h6" color="text.secondary">
                  No roles found
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={3}>
                {roles.map((role, idx) => (
                  <Grid size={{ xs: 12, sm: 6, md: 3 }} key={role.id}>
                    <RoleCard
                      role={role}
                      onManagePermissions={handleManagePermissions}
                      onDelete={handleDeleteRoleClick}
                      index={idx}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </motion.div>
        ) : (
          selectedRole && (
            <motion.div
              key="permissions-management"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <PermissionsManagement
                role={selectedRole}
                onBack={handleBackToRoles}
                onUpdatePermissions={handleUpdatePermissions}
              />
            </motion.div>
          )
        )}
      </AnimatePresence>

      <AddRoleModal
        open={addRoleModalOpen}
        onClose={() => setAddRoleModalOpen(false)}
        onSave={handleAddRole}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the role{" "}
            <strong>&quot;{roleToDelete?.name}&quot;</strong>? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ pb: 2, pr: 3 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="contained"
            sx={{
              backgroundColor: reduxTheme.primary_colors[400],
              borderRadius: 2,
              mr: 1,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              backgroundColor: reduxTheme.primary_colors[400],
              borderRadius: 2,
              
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RolesPermissions;
